import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { segment } from '@actions/analytics';
import Button from 'aquilla/core/molecules/Button';
import IconButton from 'aquilla/core/molecules/IconButton';
import {
  BATCH_KEYS,
  BATCH_BROCHURE_DOWNLOADED,
  BATCH_BROCHURE_DOWNLOAD
} from '@constants/segment';
import { evtNameCategoryMap } from '@constants/index';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useAllTypesOfCourseEventProps from '@hooks/useAllTypesOfCourseEventProps';

import { mediaQuery, Spacings } from '@styles/index';
import useBatchDetails from '@api/hooks/batch/useBatchDetails';
import CloudDownload from '@icon/CloudDownload';
import { useIsMobile } from '@stores/AppCommonData';
import { useRouter } from 'next/router';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import segmentStorage from '@events/segmentStorage';
import { useEffect, useRef } from 'react';
import { getIsStorePath } from './utils';

const StyledDownload = styled(CloudDownload)`
  margin-bottom: -${Spacings.SPACING_1B};
`;

const StyButton = styled(Button)`
  line-height: 22px;
  ${mediaQuery.uptoMobile} {
    line-height: 20px;
  }
`;

const BatchBrochureDownload = ({
  batchUID,
  isIconButton = false,
  width,
  hasStorePurchase
}) => {
  const { batchDetails } = useBatchDetails(batchUID);
  const batchGoalUID = batchDetails?.goal?.uid;
  const brochure = batchDetails?.brochure;
  const isSellable = batchDetails?.isSellable;
  const isMobile = useIsMobile();
  const goalSegmentData = useGoalEventProps({ goalUID: batchGoalUID });
  const segmentData = useAllTypesOfCourseEventProps({
    course: batchDetails,
    isBatch: true
  });
  const { route } = useRouter();
  const isStoreRoute = getIsStorePath(route);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { openElement: openLoginDrawer, isOpen: isLoginDrawerOpen } =
    useToggleable(LOGIN_DRAWER);
  const downloadButtonClicked = useRef(false);

  const dowlaodBrochure = () => {
    segment.track(
      BATCH_BROCHURE_DOWNLOADED,
      {
        ...goalSegmentData,
        ...segmentData,
        batch_state: batchDetails.state,
        last_primary_source: isStoreRoute ? 'Store' : 'Goal Home',
        is_sellable: isSellable,
        is_purchased: hasStorePurchase,
        is_store_exclusive: batchDetails?.isStoreExclusive
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      BATCH_KEYS
    );
    window.open(brochure, '_blank');
  };
  useEffect(() => {
    if (isLoggedIn && downloadButtonClicked.current) {
      dowlaodBrochure();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isLoginDrawerOpen) {
      downloadButtonClicked.current = false;
    }
  }, [isLoginDrawerOpen]);

  if (!brochure) return null;

  const handleDownload = () => {
    if (!isLoggedIn) {
      downloadButtonClicked.current = true;
      segmentStorage.setItem('lpss', BATCH_BROCHURE_DOWNLOAD);
      openLoginDrawer();
    } else dowlaodBrochure();
  };

  return (
    <>
      {isIconButton && !isMobile ? (
        <IconButton
          variant="secondary"
          color="white"
          size="medium"
          onClick={handleDownload}
          isIconButton
        >
          <CloudDownload size={24} color="var(--color-text-primary)" />
        </IconButton>
      ) : (
        <StyButton
          variant="secondary"
          startIcon={<StyledDownload size={isMobile ? 20 : 24} />}
          onClick={handleDownload}
          size="large"
          $width={width}
        >
          {width === '50%' ? 'Brochure' : 'Download brochure'}
        </StyButton>
      )}
    </>
  );
};

export default BatchBrochureDownload;

BatchBrochureDownload.propTypes = {
  batchUID: PropTypes.string.isRequired
};
